import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  HammerGestureConfig,
  HAMMER_GESTURE_CONFIG,
} from '@angular/platform-browser';
import { animate, keyframes, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import Hammer from 'hammerjs';
import { ApiService } from 'src/app/services/API/api.service';
import { EventService } from 'src/app/services/Events/events.service';
import { CachingService } from './../../services/cache/caching.service';
import {
  ANIMATION_TIMER,
  GlobalService,
} from 'src/app/services/global/global.service';
import * as kf from 'src/app/utils/keyframes';
export class MyHammerConfig extends HammerGestureConfig {
  overrides = {
    swipe: { direction: Hammer.DIRECTION_VERTICAL }, // Enable vertical swipe
  };
}

@Component({
  selector: 'app-feature-showcase',
  templateUrl: './feature-showcase.component.html',
  styleUrls: ['./feature-showcase.component.scss'],
  providers: [{ provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig }],
  animations: [
    trigger('cardAnimator', [
      transition(
        '* => slideOutUp',
        animate(ANIMATION_TIMER, keyframes(kf.slideOutUp))
      ),
      transition(
        '* => slideInDown',
        animate(ANIMATION_TIMER, keyframes(kf.slideInDown))
      ),
    ]),
  ],
})
export class FeatureShowcaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('newFeatureContainer') newFeatureContainer: ElementRef;

  @Input() selectedFeature: any;
  @Input() selectedFeatureCategory: any;
  @Input() featureMesh: any;
  allFeatures: any[] = [];
  features: any;
  featuresLength: any;
  featureCategorySelected: any;
  currentIndex;
  currentIndexAll;
  isFeaturePlaying: boolean;
  categories: any[];
  featurePlayed = [];
  ambientSound = new Audio('assets/sounds/ambient_sound.mp3');
  muteMusic = false;
  subscription: Subscription;
  limit = 60;
  isExpanded = false;
  swipeCount = 0;
  showSwipeMore = true;
  timeoutDuration = 20000;
  swipeTimeout: any;
  constructor(
    private modalController: ModalController,
    private cachingService: CachingService,
    private events: EventService,
    private apiService: ApiService,
    public globalService: GlobalService
  ) {
    this.resetSwipeTimeout();
  }

  @HostListener('swipe', ['$event'])
  onSwipe(event: any) {
    this.resetSwipeTimeout(); // Reset the timeout on swipe
  }

  resetSwipeTimeout() {
    if (this.swipeTimeout) {
      clearTimeout(this.swipeTimeout); // Clear the existing timeout
    }

    // Set a new timeout
    this.swipeTimeout = setTimeout(() => {
      this.showSwipeMore = true;
      this.swipeCount = 0;
    }, this.timeoutDuration);
  }

  async ngOnInit() {
    const isMuteFromSession = await this.cachingService.getDataFromSession(
      'muteMusic'
    );
    if (isMuteFromSession) {
      this.ambientSound.volume = 0.15; //descrease volume intensity
      this.ambientSound.play().catch(error => console.log('Audio play error:', error));
      this.ambientSound.muted = true;
      this.ambientSound.loop = true;
      this.muteMusic = isMuteFromSession;
    } else {
      this.ambientSound.volume = 0.15; //descrease volume intensity
      this.ambientSound.play().catch(error => console.log('Audio play error:', error));
      this.ambientSound.loop = true;
    }
    if (this.selectedFeature) {
      this.getFeatureFromSelection();
      this.apiService.logGAEvent(
        'uaevent',
        'Content',
        this.selectedFeature?.sub_category === 'exterior'
          ? 'Click:ExteriorFeatures'
          : 'Click:InteriorFeatures',
        this.selectedFeature?.feature_name
      );
      this.apiService.visualizerAnalytics(
        'feature',
        String(this.selectedFeature?.feature_id)
      );
    } else if (this.featureMesh) {
      this.getFeatureFromHotspot();
      this.apiService.visualizerAnalytics('feature', this.featureMesh);
    }

    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      'Click::Action',
      'Ambient Sound::On'
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.globalService.isFeatureView = false;
    }, 10);
    try {
      this.ambientSound.pause();
    } catch (error) {
      console.log('Audio pause error:', error);
    }
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      'Click::Action',
      'Ambient Sound::Off'
    );

    // Clear timeout when the component is destroyed
    if (this.swipeTimeout) {
      clearTimeout(this.swipeTimeout);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.globalService.isFeatureView = true;
    }, 10);
    const hammer = new Hammer(this.newFeatureContainer.nativeElement);
    hammer.get('swipe').set({ direction: Hammer.DIRECTION_VERTICAL });

    hammer.on('swipeup', async () => await this.onSwipeUp());
    hammer.on('swipedown', async () => await this.onSwipeDown());
  }

  async onSwipeUp() {
    this.globalService.startAnimation('slideOutUp').then((res) => {
      if (res) {
        this.swipeCount += 1;
        if (this.swipeCount > 2) this.showSwipeMore = false;
        this.goToNextFeature();
      }
    });
  }

  async onSwipeDown() {
    this.swipeCount += 1;
    if (this.swipeCount > 2) this.showSwipeMore = false;
    this.goToPreviousFeature();
    this.globalService.startAnimation('slideInDown');
  }

  updateText(text) {
    if (text.length > this.limit) {
      const truncatedText = text.substring(0, this.limit);
      const remainingText = text.substring(this.limit);
      return `${truncatedText}${this.isExpanded ? remainingText : '...'}`;
    } else {
      const truncatedText = text;
      const remainingText = '';
      return `${truncatedText}${this.isExpanded ? remainingText : '...'}`;
    }
  }

  toggleView() {
    this.isExpanded = !this.isExpanded;
  }

  muteAudio() {
    this.muteMusic = !this.muteMusic;
    this.cachingService.setDataInSession('muteMusic', this.muteMusic);
    this.ambientSound.muted = this.muteMusic;
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      'Click::Action',
      this.muteMusic ? 'Ambient Sound::Mute' : 'Ambient Sound::Unmute'
    );
    this.apiService.sendReportingData({
      eName: 'click',
      eCat: 'features_page',
      eAct: 'speaker_button_clicked',
      eVal: this.muteMusic ? 'off' : 'on',
    });
  }
  getFeatureFromSelection() {
    this.getFeatures();
    // this.subscription = this.events.subscribe('playNext', (data) => {
    //   this.subscribeToPlayEvents(data);
    // });

    this.events.subscribe('events', (data) => {
      this.subscribeToEvents(data);
    });
    this.events.subscribe('close_mobile_feature_view', (data) => {
      if (data) {
        this.closeModal();
      }
    });
    this.events.publish('events', { featureShowcase: true });
  }
  async getFeatureFromHotspot() {
    const features = await this.cachingService.getDataFromSession(
      'variant_details'
    );
    this.features = features.features;
    const selectedFeature = [];
    this.features.filter((feat) => {
      const feature = feat.featureList.filter(
        (selectedFeat) => selectedFeat.hotspot_name === this.featureMesh
      )[0];
      if (feature) {
        selectedFeature.push(feature);
      }
    });
    this.selectedFeature = selectedFeature?.[0] ?? {};
    this.selectedFeatureCategory = this.selectedFeature?.category_name ?? '';
    this.getFeatureFromSelection();
    this.events.publish('events', { featureShowcase: true });
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      this.selectedFeature?.sub_category === 'exterior'
        ? 'Click::ExteriorHotspots'
        : 'Click::InteriorHotspots',
      this.selectedFeature?.feature_name
    );
  }

  subscribeToEvents(data) {
    const key = Object.keys(data)[0];
    switch (key) {
      case 'featureIsPlaying':
        this.isFeaturePlaying = data.featureIsPlaying;
        break;
      default:
        break;
    }
  }
  subscribeToPlayEvents(data) {
    const key = Object.keys(data)[0];
    switch (key) {
      case 'playNext':
        this.playNext();
        break;
      default:
        break;
    }
  }

  playNext() {
    this.goToNextFeature();
  }

  async getFeatures() {
    const features = await this.cachingService.getDataFromSession(
      'variant_details'
    );
    this.features = features.features;
    for (const featureCategory of features.features) {
      this.allFeatures = [...this.allFeatures, ...featureCategory.featureList];
    }
    this.selectFeatureCategory(this.selectedFeatureCategory);
    this.getFeaturesCategories();
  }

  getFeaturesCategories() {
    this.categories = this.features.map((a) => a.featureCategory);
  }

  selectFeatureCategory(featureCategory) {
    this.events.publish('events', { featureDetails: this.selectedFeature });
    this.featurePlayed.push(this.selectedFeature.feature_id);

    this.featureCategorySelected = featureCategory;
    const selectedCategoryFeatures = this.features.filter(
      (el) => el.featureCategory === featureCategory
    )[0];
    this.featuresLength = selectedCategoryFeatures.featureList;
    this.currentIndex = selectedCategoryFeatures.featureList.findIndex(
      (e) => e.feature_id === this.selectedFeature.feature_id
    );
    this.currentIndexAll = this.allFeatures.findIndex(
      (e) => e.feature_id === this.selectedFeature.feature_id
    );
    this.isExpanded = false;
    this.apiService.sendReportingData({
      eName: 'click',
      eCat: 'features_page',
      eAct: this.featureCategorySelected,
      eVal: this.selectedFeature.feature_id,
    });
  }

  goToNextFeature() {
    const features = this.features.filter(
      (el) => el.featureCategory === this.featureCategorySelected
    )[0];
    this.selectedFeature = features.featureList[this.currentIndex + 1];
    if (this.currentIndex + 1 !== features.featureList.length) {
      this.selectFeatureCategory(this.featureCategorySelected);
    } else {
      const categoryIndex = this.categories.findIndex(
        (a) => a === this.featureCategorySelected
      );

      if (this.features[categoryIndex + 1]?.featureList[0]) {
        this.selectedFeature = this.features[categoryIndex + 1].featureList[0];
        this.selectFeatureCategory(this.categories[categoryIndex + 1]);
      } else {
        this.closeModal();
      }
    }
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      this.selectedFeature?.sub_category === 'exterior'
        ? 'Click:ExteriorFeatures'
        : 'Click:InteriorFeatures',
      this.selectedFeature?.feature_name
    );
    this.apiService.visualizerAnalytics(
      'feature',
      String(this.selectedFeature?.feature_id)
    );
  }
  goToPreviousFeature() {
    // this.subscription.unsubscribe();
    // this.events.subscribe('events', (data) => {
    //   this.subscribeToEvents(data);
    // });
    const features = this.features.filter(
      (el) => el.featureCategory === this.featureCategorySelected
    )[0];
    this.selectedFeature = features.featureList[this.currentIndex - 1];
    if (this.currentIndexAll === 0) {
      return this.closeModal();
    }
    if (this.currentIndex !== 0) {
      this.selectFeatureCategory(this.featureCategorySelected);
    } else {
      const categoryIndex = this.categories.findIndex(
        (a) => a === this.featureCategorySelected
      );
      const lengthOfLastPlayedFeatures =
        this.features[categoryIndex - 1].featureList.length;
      this.selectedFeature =
        this.features[categoryIndex - 1].featureList[
          lengthOfLastPlayedFeatures - 1
        ];
      this.selectFeatureCategory(this.categories[categoryIndex - 1]);
    }
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      this.selectedFeature?.sub_category === 'exterior'
        ? 'Click:ExteriorFeatures'
        : 'Click:InteriorFeatures',
      this.selectedFeature?.feature_name
    );
    this.apiService.visualizerAnalytics(
      'feature',
      String(this.selectedFeature?.feature_id)
    );
  }

  selectFeature(category: any, feature: any) {
    this.selectedFeature = feature;
    this.selectFeatureCategory(category.featureCategory);

    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      this.selectedFeature?.sub_category === 'exterior'
        ? 'Click:ExteriorFeatures'
        : 'Click:InteriorFeatures',
      this.selectedFeature?.feature_name
    );
    this.apiService.visualizerAnalytics(
      'feature',
      String(this.selectedFeature?.feature_id)
    );
  }

  async closeModal() {
    // const pin_no = await this.cachingService.getDataFromSession('pin_no');
    // const uniqueFeatures = [...new Set(this.featurePlayed)];
    // let requestBody = {
    //   pin_no: pin_no[this.globalService.vehicleCode],
    //   features: uniqueFeatures,
    // };
    // this.apiService
    //   .post('configuration/visualizer/save-features', requestBody)
    //   .then(
    //     (res: any) => {
    //       if (res.message === 'success') {
    //         this.globalService.dismissModal();
    //       } else {
    //         this.globalService.dismissModal();
    //       }
    //     },
    //     (err) => {
    //       this.globalService.dismissModal();
    //     }
    //   );
    this.events.publish('events', { featureShowcase: false });
    this.events.publish('events', { featureDetails: null });
    this.events.publish('events', { closeFeatureShowCase: null });
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      'Click',
      'Close button on features'
    );
    // this.subscription.unsubscribe();

    this.globalService.dismissModal(true);
  }
}
